'use client';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { Virtualizer } from '@tanstack/react-virtual';
import { throttle } from '@wwe-portal/ui/external/throttle';

const calculateActiveId = (
  virtualizer: Virtualizer<Window, Element>,
  categoryIndexes: Record<string, number>
) => {
  const scrollY = virtualizer.scrollElement?.scrollY;
  const height = virtualizer.scrollRect.height;

  if (scrollY) {
    const item = virtualizer.getVirtualItemForOffset(
      scrollY + (height * 2) / 3
    );
    const entries = Object.entries(categoryIndexes).sort((a, b) => a[1] - b[1]);

    const lastIndex = entries[entries.length - 1]?.[1];
    if (lastIndex != null && item.index >= lastIndex) {
      return entries[entries.length - 1]?.[0];
    }
    const foundEntryIndex = Math.max(
      entries.findIndex(([, value]) => value >= item.index) - 1,
      0
    );
    return entries[foundEntryIndex]?.[0];
  }
  return undefined;
};

export const useNavigation = ({
  virtualizer,
  categoryIndexes,
  itemIds,
}: {
  virtualizer: Virtualizer<Window, Element>;
  categoryIndexes: Record<string, number>;
  itemIds: {
    id: string;
    label: string;
    icon: ReactNode;
  }[];
}) => {
  const [activeId, setActiveId] = useState<string>();

  useEffect(() => {
    const handleScroll = throttle(() => {
      setActiveId(calculateActiveId(virtualizer, categoryIndexes));
    }, 1000);

    window.document.addEventListener('scroll', handleScroll);
    return () => {
      window.document.removeEventListener('scroll', handleScroll);
    };
  }, [virtualizer, categoryIndexes]);

  const handleClick = useCallback(
    ({ id }: { id: string | undefined }) => {
      if (id != null) {
        virtualizer.scrollToIndex(categoryIndexes[id] ?? 0, {
          align: 'center',
        });
        setActiveId(id);
      }
    },
    [virtualizer, categoryIndexes]
  );

  return {
    itemIds,
    activeId,
    onClick: handleClick,
  };
};
